<template>
  <validation-provider
    v-slot="{ errors }"
    :name="$attrs.name"
    :rules="iRules"
    :vid="vid"
  >
    <v-label
      v-if="fixedLabel"
      :dark="fixedLabelDark"
    >
      {{ displayLabel }}
    </v-label>
    <v-combobox
      v-model="iValue"
      v-bind="$attrs"
      :attach="attach"
      :background-color="backgroundColor"
      :clear-icon="clearIcon"
      :clearable="clearable"
      :dense="dense"
      :error="validation && errors.length > 0"
      :error-messages="errors"
      :hide-details="!validation || errors.length === 0"
      :items="options"
      :label="(!fixedLabel && displayLabel) || ''"
      :loading="isLoading"
      :multiple="multiple"
      :outlined="outlined"
      :search-input.sync="search"
      :prefix="$t(prefix)"
      v-on="$listeners"
      @change="search = ''"
    >
      <template #no-data>
        <v-list-item v-if="search">
          <span>{{ search }} <small>({{ $t('common.new') }})</small></span>
        </v-list-item>
      </template>
      <template
        v-if="customItem"
        #item="data"
      >
        <slot
          name="item"
          v-bind="data"
        />
      </template>
      <template
        v-if="customSelection"
        #selection="data"
      >
        <slot
          name="selection"
          v-bind="data"
        />
      </template>
      <template
        v-else-if="multiple"
        #selection="data"
      >
        <v-chip
          v-bind="data.attrs"
          :input-value="data.selected"
          close-icon="far fa-times-circle"
          close
          small
          @click:close="remove(data.item)"
        >
          {{ data.item[itemText] }}
        </v-chip>
      </template>
    </v-combobox>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import { useValidation } from '@/compositions/validation'
import { useFieldStore } from '@/compositions/fieldStore'
import dot from 'dot-object'

export default {
  components: {
    ValidationProvider
  },
  props: {
    attach: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: 'white'
    },
    clearIcon: {
      type: String,
      default: 'far fa-times-circle'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    customItem: Boolean,
    customSelection: Boolean,
    dense: {
      type: Boolean,
      default: true
    },
    fixedLabel: Boolean,
    fixedLabelDark: Boolean,
    items: {
      type: Array,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    loading: {
      type: [String, Boolean],
      default: null
    },
    multiple: Boolean,
    outlined: {
      type: Boolean,
      default: true
    },
    prefix: {
      type: String,
      default: null
    },
    remote: {
      type: Object,
      default: null
    },
    returnObject: {
      type: Boolean,
      default: true
    },
    rules: {
      type: [String, Object],
      default: () => ({})
    },
    storeKey: {
      type: String,
      default: null
    },
    validation: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Number, String, Array, Object],
      default: null
    },
    vid: {
      type: String,
      default: null
    }
  },
  setup (props) {
    const { iRules, displayLabel } = useValidation(props)
    const { store, iStoreKey } = useFieldStore(props)

    return { store, iStoreKey, iRules, displayLabel }
  },
  data () {
    return {
      fetching: false,
      search: null,
      asyncItems: [],
      toAdd: [],
      pagination: {
        page: null,
        lastPage: null,
        perPage: null
      }
    }
  },
  computed: {
    isLastPage () {
      const { lastPage, page } = this.pagination
      return !page || lastPage === page
    },
    isRemote () {
      return !!this.remote ?? false
    },
    isLoading () {
      return this.loading || this.fetching
    },
    options () {
      if (this.isRemote) {
        return this.asyncItems
      } else if (this.storeKey) {
        return dot.pick(this.iStoreKey, this.store) ?? []
      } else if (this.items) {
        return this.items
      }
      return []
    },
    iValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    search: {
      handler (value, prevVal) {
        if (this.isRemote && value !== undefined && value !== prevVal) {
          this.fetchEntriesDebounced(value)
        }
      }
    }
  },
  async created () {
    this.$attrs.dataTest = undefined
    if (this.isRemote) {
      if (this.value) {
        if (Array.isArray(this.value)) {
          this.toAdd = [...this.value]
        } else if (typeof this.value === 'object') {
          this.toAdd.push(this.value)
        }
      }
      try {
        this.fetching = true
        await this.fetch()
        if (this.value) {
          await this.checkInitialValue()
        }
      } finally {
        this.fetching = false
      }
    }
  },
  methods: {
    fetch (v) {
      const params = {}

      this.fetching = true
      if (this.remote.params) {
        // eslint-disable-next-line prefer-const
        for (let [key, value] of Object.entries(this.remote.params)) {
          if (key !== 'searchParam') {
            params[key] = value
          }
        }
      }

      if (v && this.remote?.params?.searchParam) {
        params[this.remote.params.searchParam] = v
      }

      params.resultsPerPage = this.remote?.params?.maxItems ||
        window.env.AUTOCOMPLETE_MAX_ITEMS

      return this.$axios({
        method: this.remote.method,
        url: this.remote.url,
        params
      }).then(res => {
        const results = res.data.data
        this.asyncItems = []
        if (Array.isArray(results)) {
          this.asyncItems = [...this.toAdd, ...results]
          this.toAdd = []
          this.pagination.page = res.data.meta.current_page
          this.pagination.lastPage = res.data.meta.last_page
          this.pagination.perPage = res.data.meta.per_page
        }
      }).catch(err => {
        throw new Error(err)
      }).finally(() => (this.fetching = false))
    },
    fetchEntriesDebounced (value) {
      this.$debounce(this.fetch, value)
    },
    remove (field) {
      this.iValue = this.iValue
        .filter(f => {
          if (this.returnObject) {
            return typeof this.$attrs.itemValue === 'function'
              ? this.$attrs.itemValue(f) !== this.$attrs.itemValue(field)
              : f[this.$attrs.itemValue] !== field[this.$attrs.itemValue]
          }
          return f !== field[this.$attrs.itemValue]
        })
    },
    getById (id) {
      const url = this.remote.urlFind.replace(`:${this.remote.urlFindKey}`, id)
      return this.$axios({
        method: 'GET',
        url,
        params: this.remote.urlFindParams
      }).then(res => res.data.data)
        .catch(err => {
          throw new Error(err)
        })
    },
    async getNotFoundItem (v) {
      if (this.remote.urlFind && this.remote.urlFindKey) {
        const id = v.id || v
        const found = this.asyncItems.find(f =>
          dot.pick(this.remote.urlFindKey, f) === id)
        if (!found) {
          const item = await this.getById(id)
          if (item) {
            const prepend = Array.isArray(item) ? item : [item]
            this.asyncItems = [...prepend, ...this.asyncItems]
          }
        }
      }
    },
    checkInitialValue () {
      this.multiple
        ? this.value.forEach(v => { this.getNotFoundItem(v) })
        : this.getNotFoundItem(this.value)
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-autocomplete) {
  .v-input__icon--clear .v-icon--link {
    font-size: 1.1rem !important;
  }

  .v-chip {
    margin-top: 5px;
  }
}
</style>
